import { synctoERP } from '@/adapters/master';
import {
  addProduct,
  matchProduct,
  searchProducts,
  updateProduct,
} from '@/adapters/product';
import { IDataModal } from '@/interfaces/data-modal';
import { IProduct, IProductRes } from '@/interfaces/product';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { uniqueId } from 'lodash';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

const _modal: IDataModal<IProduct> = {
  open: false,
};

const ProductPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IProduct[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<IProduct[]>([]);
  const [modal, setModal] = useState<IDataModal<IProduct>>({ ..._modal });
  const [form] = Form.useForm();

  const { data, refetch } = useQuery<IProductRes, Error>({
    queryKey: ['search-product-list'],
    queryFn: searchProducts,
    retry: 0,
  });

  const actionProductMutation = useMutation({
    mutationFn: async (data: Partial<IProduct>) =>
      action === 'create' ? await addProduct(data) : await updateProduct(data),
    mutationKey: ['add-product'],
    onSuccess: data => {
      if (data.status === 1) {
        notification.success({ message: 'Sync Product สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Product ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedProducts = async () => {
    for (const product of selectedRows) {
      await actionProductMutation.mutateAsync(product);
    }
  };

  const handleUpdateCode = useCallback((item: IProduct) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IProduct, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสสินค้า',
          dataIndex: 'PT_PART',
          className: 'fw-bold',
          filterSearch: true,
          filterMode: 'menu',
          onFilter: (value, record) => record.PT_PART.includes(value as string),
        },
        {
          title: 'SKU',
          dataIndex: 'PT_SKU',
          className: 'fw-semibold',
        },
        {
          title: 'รายละเอียด 1',
          dataIndex: 'PT_DESC1',
          className: 'fw-semibold',
        },
        {
          title: 'รายละเอียด 2',
          dataIndex: 'PT_DESC2',
          render: _ => <div dangerouslySetInnerHTML={{ __html: _ }} />,
        },
        {
          title: 'รายละเอียด 3',
          dataIndex: 'PT_DESC3',
          hidden: action === 'match',
        },
        {
          title: 'Bento Detail',
          dataIndex: 'PT_DETAIL_WEB',
          hidden: action === 'match',
        },
        {
          title: 'หน่วยสินค้า',
          dataIndex: 'PT_UM',
          hidden: action === 'match',
        },
        {
          title: 'คลัง',
          dataIndex: 'PT_LOC',
          hidden: action === 'match',
        },
        {
          title: 'แคตตาลอค',
          dataIndex: 'CAT_DESCRIPTION',
          hidden: action === 'match',
        },
        {
          title: 'แบรนด์',
          dataIndex: 'BRAND_DESC',
          hidden: action === 'match',
        },
        {
          title: 'ขนาด',
          dataIndex: 'PT_PTSIZE',
          hidden: action === 'match',
        },
        {
          title: 'สี',
          dataIndex: 'PT_COLOR',
          hidden: action === 'match',
        },
        {
          title: 'BENTO ID',
          dataIndex: 'PT_SING_ID',
        },
        {
          title: '',
          dataIndex: 'PT_SING_ID',
          align: 'center',
          width: 120,
          hidden: action !== 'match',
          render: (_, rec) => (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => handleUpdateCode(rec)}
            >
              UPDATE
            </button>
          ),
        },
      ] as TableColumnsType<IProduct>,
    [handleUpdateCode, action],
  );

  const insert = data?.data?.insert;
  const update = data?.data?.update;
  const match = data?.data?.match;

  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(
      listMap[action]?.map(c => ({ ...c, _key: uniqueId('p-') })) || [],
    );
    setSelectedRowKeys([]);
  }, [action, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IProduct[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IProduct) => ({
      disabled: action === 'match',
    }),
  };
  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  // Filter selected data using "between" search for PT_PART
  const filteredData = useMemo(() => {
    return selected.filter(item => {
      if (action === 'match') {
        const brandName = (item.PT_DESC1 || '').toLowerCase();
        const searchValue = search.trim().toLowerCase();
        return !searchValue || brandName.includes(searchValue);
      } else {
        const productCode = (item.PT_PART || '').toUpperCase();

        // Convert search values to uppercase for case-insensitive comparison
        const fromValue = searchText1?.trim().toUpperCase() || '';
        const toValue =
          searchText2?.trim().toUpperCase() || 'ZZZZZZZZZZZZZZZZZZZZ';

        // If both fields are empty, show all
        if (!fromValue && toValue === 'ZZZZZZZZZZZZZZZZZZZZ') {
          return true;
        }

        // If only one field has a value, check if product code contains that value
        if (fromValue && !searchText2.trim()) {
          return productCode.includes(fromValue);
        }

        if (!fromValue && searchText2.trim()) {
          return productCode.includes(toValue);
        }

        // For true BETWEEN comparison with both values
        // Check if any part of the product code is between the values
        for (let i = 0; i < productCode.length; i++) {
          for (let j = i + 1; j <= productCode.length; j++) {
            const subCode = productCode.substring(i, j);
            if (subCode >= fromValue && subCode <= toValue) {
              return true;
            }
          }
        }
        return false;
      }
    });
  }, [selected, action, search, searchText1, searchText2]);

  const matchBrandApi = useMutation({
    mutationFn: async (data: Partial<IProduct>) =>
      action === (await matchProduct(data)),
    mutationKey: ['match-product'],
    onSuccess: data => {
      setModal({ ..._modal });
      notification.success({ message: 'Match Product สำเร็จ' });
      refetch();
      form.resetFields();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onFinish: FormProps<IProduct>['onFinish'] = async values => {
    await matchBrandApi.mutateAsync({
      ...values,
      PT_SING_ID: modal.item?.PT_SING_ID,
      PT_VARIANT_SING_ID: modal.item?.PT_VARIANT_SING_ID,
      PT_DESC2: modal.item?.PT_DESC2,
    });
  };

  const actionTOERP = useMutation({
    mutationFn: async () => await synctoERP({ MASTER_TYPE: 'PRODUCT' }),
    mutationKey: ['sync-to-erp'],
    onSuccess: data => {
      notification.success({ message: 'Sync Product สำเร็จ' });
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });
  const handleSyncTOERP = async () => {
    await actionTOERP.mutateAsync();
  };

  return (
    <div>
      <Flex justify="space-between" gap={12} className="mb-3">
        <Flex align="start" gap={12}>
          <Radio.Group
            value={action}
            onChange={e => {
              setAction(e.target.value);
              setSearchText1('');
              setSearchText2('');
            }}
            size="large"
          >
            <Radio.Button value="match">
              จับคู่ <Badge count={match?.length || 0} />
            </Radio.Button>
            <Radio.Button value="create">
              เพิ่ม <Badge count={insert?.length || 0} />
            </Radio.Button>
            <Radio.Button value="update">
              แก้ไข/ปรับปรุง <Badge count={update?.length || 0} />
            </Radio.Button>
          </Radio.Group>
          {action !== 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>จาก</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText1}
                onChange={e => {
                  setSearchText1(e);
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.PT_PART)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
              <div>ถึง</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText2}
                onChange={e => {
                  setSearchText2(e);
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.PT_PART)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}
          {action === 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                style={{ minWidth: 300 }}
                value={search}
                onChange={e => {
                  SetSearch(e || '');
                }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.PT_DESC1)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}
        </Flex>
        <Flex gap={12}>
          <button
            type="button"
            className="btn btn-info"
            style={{ minWidth: 100 }}
            onClick={() => handleSyncTOERP()}
          >
            {actionTOERP.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              '< ดึงข้อมูลมา ERP'
            )}
          </button>
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedProducts()}
          >
            {actionProductMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              'ส่งข้อมูลไป BENTO >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IProduct>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          loading={matchBrandApi.isPending}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                onFinish(values);
              })
              .catch(() => {
                // validation failed
              });
          }}
        >
          <Form
            form={form}
            name="brand"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<IProduct>
              label="PT PART"
              name="PT_PART"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(brand => ({
                  label: `${brand.PT_PART} - ${brand.PT_DESC1 || ''}`,
                  value: brand.PT_PART,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default ProductPage;
